import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RegistryData } from '../../../utils/useRegistry';
import { useIsMobile } from '../../../utils/useIsMobile';

interface AssetGenInfoPanelProps {
  registry: RegistryData;
  assetType: 'character' | 'scene' | 'prop';
  assetName: string;
  // 该状态图对应的集数；为空/未提供（基础图）时回退为该资产出现的全部集数。
  episodes?: number[];
}

const tagStyle: React.CSSProperties = {
  fontSize: '0.72rem', padding: '1px 8px', borderRadius: '999px',
  background: 'var(--surface-2)', color: 'var(--text-2)', border: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};

export default function AssetGenInfoPanel({ registry, assetType, assetName, episodes }: AssetGenInfoPanelProps) {
  const { t } = useTranslation('projectDetail');
  const isCharacter = assetType === 'character';
  const [tab, setTab] = useState<'summary' | 'character'>('summary');
  const isMobile = useIsMobile();

  const character = useMemo(
    () => (isCharacter ? registry.characters.find(c => c.name === assetName) : undefined),
    [isCharacter, registry, assetName],
  );

  // 目标集数：状态图用其出现集数；基础图（未传）回退为该资产出现的全部集数
  const targetEpisodes = useMemo(() => {
    if (episodes && episodes.length > 0) return Array.from(new Set(episodes)).sort((a, b) => a - b);
    if (isCharacter) return [...(character?.appeared_in_episodes ?? [])].sort((a, b) => a - b);
    const sp = registry.sceneProps.find(s => s.name === assetName);
    return [...(sp?.appeared_in_episodes ?? [])].sort((a, b) => a - b);
  }, [episodes, isCharacter, character, registry, assetName]);

  const targetSet = useMemo(() => new Set(targetEpisodes), [targetEpisodes]);

  const epSummaries = useMemo(
    () => registry.episodes.filter(e => targetSet.has(e.episode)).sort((a, b) => a.episode - b.episode),
    [registry, targetSet],
  );

  // 角色状态时间线：取在目标集生效的条目（episode_start <= 该集的最近一条）
  const timeline = useMemo(() => {
    if (!character) return [] as Array<{ episode_start: number; states: Record<string, string> }>;
    const tl = [...character.state_timeline].sort((a, b) => a.episode_start - b.episode_start);
    const idxSet = new Set<number>();
    for (const ep of targetEpisodes) {
      let idx = -1;
      for (let i = 0; i < tl.length; i++) {
        if (tl[i].episode_start <= ep) idx = i;
        else break;
      }
      if (idx >= 0) idxSet.add(idx);
    }
    return Array.from(idxSet).sort((a, b) => a - b).map(i => tl[i]);
  }, [character, targetEpisodes]);

  const relationships = character ? Object.entries(character.relationships || {}) : [];

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '6px 8px', fontSize: '0.8rem', fontWeight: active ? 700 : 500,
    cursor: 'pointer', background: 'none', border: 'none',
    color: active ? 'var(--theme-character)' : 'var(--text-3)',
    borderBottom: `2px solid ${active ? 'var(--theme-character)' : 'transparent'}`,
  });

  const summaryContent = (
    epSummaries.length === 0 ? (
      <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('genInfo.noData')}</p>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        {epSummaries.map(ep => (
          <div key={ep.episode}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--theme-character)', marginBottom: '3px' }}>
              {t('episodes.episodeN', { n: ep.episode })}
            </div>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.6 }}>{ep.summary}</p>
          </div>
        ))}
      </div>
    )
  );

  const characterContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.4rem' }}>{t('genInfo.stateTimeline')}</div>
        {timeline.length === 0 ? (
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('genInfo.noData')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {timeline.map((entry, i) => (
              <div key={`${entry.episode_start}-${i}`}>
                <div style={{ fontSize: '0.74rem', color: 'var(--theme-character)', marginBottom: '2px' }}>{t('genInfo.fromEpisode', { n: entry.episode_start })}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {Object.entries(entry.states || {}).map(([k, v]) => (
                    <div key={k} style={{ fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.5 }}>
                      <span style={{ color: 'var(--text-3)' }}>{k}：</span>{v}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.4rem' }}>{t('genInfo.relationships')}</div>
        {relationships.length === 0 ? (
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('genInfo.noData')}</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {relationships.map(([who, rel]) => (
              <span key={who} style={tagStyle} title={rel}>{who}：{rel}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) return null;

  return (
    <div style={{ width: '320px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderLeft: '1px solid var(--border)', paddingLeft: '1rem' }}>
      {isCharacter ? (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '0.75rem', flexShrink: 0 }}>
          <button style={tabBtnStyle(tab === 'summary')} onClick={() => setTab('summary')}>{t('genInfo.tabSummary')}</button>
          <button style={tabBtnStyle(tab === 'character')} onClick={() => setTab('character')}>{t('genInfo.tabCharacter')}</button>
        </div>
      ) : (
        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--theme-character)', paddingBottom: '0.5rem', marginBottom: '0.75rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {t('genInfo.tabSummary')}
        </div>
      )}
      <div className="thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '6px' }}>
        {isCharacter && tab === 'character' ? characterContent : summaryContent}
      </div>
    </div>
  );
}
